import { useRef, useState } from 'react';
import { toast } from '@/components/ui/Toast';

/**
 * קלט ייבוא לידים משותף (מסך המאגר + טאב הלידים בקמפיין): העלאת קובץ
 * CSV/TXT או הדבקה חופשית. הפירסור בצד הלקוח (המוסכמה של המודול — השרת
 * מקבל JSON): שורה לכל ליד, טלפון ואז שם, מופרדים בפסיק/טאב/נקודה-פסיק.
 * שורת כותרת (ללא טלפון) מדולגת אוטומטית; מ-Excel שומרים כ-CSV.
 */

export interface ParsedLead { phone: string; name?: string }

export function parseLeadsText(text: string): ParsedLead[] {
  const out: ParsedLead[] = [];
  for (const line of text.split('\n')) {
    const [phone, ...rest] = line.split(/[,\t;]/);
    const clean = (phone ?? '').replace(/[^\d+]/g, '');
    // שורת כותרת / שורה בלי מספר סביר — מדולגת.
    if (clean.length < 7) continue;
    const name = rest.join(' ').trim();
    out.push(name ? { phone: clean, name } : { phone: clean });
  }
  return out;
}

export function LeadImportInput({ onParsed }: { onParsed: (leads: ParsedLead[]) => void }) {
  const [text, setText] = useState('');
  const [fileName, setFileName] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement | null>(null);
  const parsed = parseLeadsText(text);

  function update(next: string, file?: string) {
    setText(next);
    setFileName(file ?? null);
    onParsed(parseLeadsText(next));
  }

  function onFile(file: File) {
    if (file.size > 10 * 1024 * 1024) { toast('קובץ עד 10MB', 'danger'); return; }
    if (/\.xlsx?$/i.test(file.name)) {
      toast('קובצי Excel — יש לשמור כ-CSV (קובץ ← שמירה בשם ← CSV) ולהעלות שוב', 'warn');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => update(String(reader.result ?? ''), file.name);
    reader.onerror = () => toast('שגיאה בקריאת הקובץ', 'danger');
    reader.readAsText(file, 'UTF-8');
  }

  return (
    <div className="stack" style={{ gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          ref={fileRef}
          type="file"
          accept=".csv,.txt,text/csv,text/plain"
          style={{ display: 'none' }}
          onChange={(e) => { const f = e.target.files?.[0]; if (f) onFile(f); e.target.value = ''; }}
        />
        <button type="button" className="btn" onClick={() => fileRef.current?.click()}>
          העלה קובץ (CSV)
        </button>
        {fileName && <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>{fileName}</span>}
        <span style={{ fontSize: 'var(--t-xs)', color: 'var(--muted)' }}>או הדבק למטה</span>
      </div>
      <textarea
        value={text}
        onChange={(e) => update(e.target.value)}
        dir="ltr"
        rows={8}
        placeholder={'0501234567,ישראל ישראלי\n0529876543,דנה כהן'}
        style={{ width: '100%', padding: 10, border: '1px solid var(--line)', borderRadius: 'var(--r-2)', background: 'var(--surface)', fontFamily: 'var(--font-mono)', resize: 'vertical' }}
      />
      <div style={{ fontSize: 'var(--t-sm)', color: parsed.length ? 'var(--ok)' : 'var(--muted)' }}>
        {parsed.length ? `${parsed.length} לידים תקינים זוהו` : 'שורה לכל ליד: טלפון, ואז שם (אופציונלי). שורת כותרת תדולג אוטומטית.'}
      </div>
    </div>
  );
}
